import { Link } from "react-router-dom";
import { 
  ArrowRight, 
  Target, 
  Eye, 
  Heart,
  CheckCircle,
  Compass,
  TrendingUp,
  Shield
} from "lucide-react";

const SobrePage = () => {
  const valores = [
    {
      icon: Shield,
      title: "Rigor Técnico",
      description: "Cada decisão é fundamentada em ciência e evidências."
    },
    {
      icon: Heart,
      title: "Humanidade",
      description: "Pessoas estão no centro de toda transformação."
    },
    {
      icon: Target,
      title: "Estratégia",
      description: "Resultados mensuráveis e alinhados ao negócio."
    },
    {
      icon: TrendingUp,
      title: "Sustentabilidade",
      description: "Mudanças que perduram no tempo."
    }
  ];

  const diferenciais = [
    "Não é terapia travestida de consultoria",
    "Não é RH operacional genérico",
    "Não é treinamento motivacional vazio",
    "É atuação estruturada, científica e personalizada"
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="sobre-page">
      {/* Hero Section */}
      <section className="py-20 lg:py-28 bg-gradient-to-br from-brand-base-clara via-[#E5DBC5] to-brand-surface relative overflow-hidden" data-testid="sobre-hero">
        {/* Decorative lines */}
        <div className="absolute right-12 lg:right-24 top-1/4 bottom-1/4 flex gap-3">
          <div className="w-[2px] h-full bg-brand-terra-humana/20" />
          <div className="w-[2px] h-full bg-brand-terracota/30 mt-8" />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="font-sans text-sm tracking-[0.15em] text-brand-muted mb-6 flex items-center gap-3">
              <span className="w-6 h-[1px] bg-brand-muted" />
              SOBRE A CONSULTORIA
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-normal text-brand-neutro leading-[1.1] mb-6">
              Psicologia organizacional aplicada à{" "}
              <span className="italic text-brand-terracota">realidade do negócio.</span>
            </h1>
            <p className="font-sans text-lg lg:text-xl text-brand-muted leading-relaxed">
              Ajudamos empresas e empreendedores a crescerem de forma saudável, estratégica e sustentável, unindo ciência, comportamento e gestão.
            </p>
          </div>
        </div>
      </section>

      {/* About Section - Dark */}
      <section className="py-20 lg:py-28 bg-brand-neutro" data-testid="sobre-profissional">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
            <div>
              <p className="font-sans text-sm tracking-[0.15em] text-brand-terracota mb-6 flex items-center gap-3">
                <span className="w-6 h-[1px] bg-brand-terracota" />
                A PROFISSIONAL
              </p>
              <h2 className="font-serif text-3xl lg:text-4xl font-normal text-brand-base-clara mb-8">
                Lylian Lopes de Carvalho
              </h2>
              <div className="space-y-6">
                <p className="font-sans text-brand-terra-humana leading-relaxed">
                  Psicóloga Organizacional com atuação voltada ao desenvolvimento estratégico de pessoas e equipes, com foco na melhoria do desempenho organizacional, fortalecimento de lideranças e construção de ambientes de trabalho mais produtivos e saudáveis.
                </p>
                <p className="font-sans text-brand-terra-humana/80 leading-relaxed">
                  Atua diretamente com empresas que buscam crescimento estruturado por meio da capacitação de colaboradores, organização de processos comportamentais e desenvolvimento de líderes mais preparados, conscientes e eficazes.
                </p>
              </div>
            </div>
            <div className="space-y-6">
              <p className="font-sans text-brand-terra-humana/80 leading-relaxed">
                Focada em consultorias, treinamentos e acompanhamento organizacional, auxilia na redução de falhas, melhoria da comunicação interna e potencialização de resultados através da gestão de pessoas.
              </p>
              <p className="font-sans text-brand-terra-humana/80 leading-relaxed">
                Integra conhecimento técnico em comportamento humano com práticas aplicáveis à realidade empresarial, oferecendo soluções objetivas, estratégicas e alinhadas às necessidades de cada negócio.
              </p>
              <div className="pt-6 border-t border-brand-terra-humana/20">
                <p className="font-serif text-xl italic text-brand-base-clara leading-relaxed">
                  "Equipes bem desenvolvidas são a base para empresas sólidas. Lideranças capacitadas são essenciais para resultados consistentes e crescimento sustentável."
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Propósito Section */}
      <section className="py-20 lg:py-28 bg-brand-base-clara" data-testid="sobre-proposito">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
            {/* Propósito */}
            <div className="bg-brand-surface p-8 lg:p-10 rounded-2xl">
              <div className="w-12 h-12 rounded-xl bg-brand-terracota/10 flex items-center justify-center mb-6">
                <Compass className="text-brand-terracota" size={24} strokeWidth={1.5} />
              </div>
              <h3 className="font-serif text-xl font-medium text-brand-neutro mb-4">
                Propósito
              </h3>
              <p className="font-sans text-brand-muted leading-relaxed">
                Transformar a relação entre pessoas e organizações, criando ambientes onde o crescimento individual impulsiona resultados coletivos sustentáveis.
              </p>
            </div>

            {/* Missão */}
            <div className="bg-brand-surface p-8 lg:p-10 rounded-2xl">
              <div className="w-12 h-12 rounded-xl bg-brand-terracota/10 flex items-center justify-center mb-6">
                <Target className="text-brand-terracota" size={24} strokeWidth={1.5} />
              </div>
              <h3 className="font-serif text-xl font-medium text-brand-neutro mb-4">
                Missão
              </h3>
              <p className="font-sans text-brand-muted leading-relaxed">
                Aplicar psicologia organizacional com rigor técnico para alinhar pessoas, cultura e resultados, gerando transformação real nas empresas que atendemos.
              </p>
            </div>

            {/* Visão */}
            <div className="bg-brand-surface p-8 lg:p-10 rounded-2xl">
              <div className="w-12 h-12 rounded-xl bg-brand-terracota/10 flex items-center justify-center mb-6">
                <Eye className="text-brand-terracota" size={24} strokeWidth={1.5} />
              </div>
              <h3 className="font-serif text-xl font-medium text-brand-neutro mb-4">
                Visão
              </h3>
              <p className="font-sans text-brand-muted leading-relaxed">
                Ser referência em consultoria de psicologia organizacional, reconhecida pela excelência técnica e pelo impacto real na transformação de organizações.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Valores Section */}
      <section className="py-20 lg:py-28 bg-gradient-to-b from-brand-surface to-brand-base-clara" data-testid="sobre-valores">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <p className="font-sans text-sm tracking-[0.15em] text-brand-terracota mb-4 flex items-center gap-3 justify-center">
              <span className="w-6 h-[1px] bg-brand-terracota" />
              NOSSOS VALORES
              <span className="w-6 h-[1px] bg-brand-terracota" />
            </p>
            <h2 className="font-serif text-3xl lg:text-4xl font-normal text-brand-neutro">
              O que nos guia
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {valores.map((valor, index) => (
              <div 
                key={index}
                className="text-center"
                data-testid={`valor-${index}`}
              >
                <div className="w-16 h-16 rounded-2xl bg-brand-base-clara flex items-center justify-center mx-auto mb-6 border border-brand-border">
                  <valor.icon className="text-brand-terracota" size={28} strokeWidth={1.5} />
                </div>
                <h3 className="font-serif text-lg font-medium text-brand-neutro mb-2">
                  {valor.title}
                </h3>
                <p className="font-sans text-sm text-brand-muted leading-relaxed">
                  {valor.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Posicionamento Section */}
      <section className="py-20 lg:py-28 bg-brand-verde" data-testid="sobre-posicionamento">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <p className="font-sans text-sm tracking-[0.15em] text-brand-base-clara/60 mb-4 flex items-center gap-3">
                <span className="w-6 h-[1px] bg-brand-base-clara/40" />
                POSICIONAMENTO
              </p>
              <h2 className="font-serif text-3xl lg:text-4xl font-normal text-brand-base-clara mb-6">
                Psicologia organizacional aplicada com rigor técnico e visão de negócio.
              </h2>
              <p className="font-sans text-brand-base-clara/80 leading-relaxed mb-8">
                O trabalho vai além de processos. É focado em transformação real. Acreditamos que empresas crescem quando pessoas crescem. Cada serviço é pensado de forma personalizada, respeitando identidade, valores e objetivos de cada cliente.
              </p>
            </div>
            <div className="space-y-4">
              {diferenciais.map((item, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-4 bg-brand-base-clara/10 backdrop-blur-sm p-4 rounded-xl"
                >
                  <CheckCircle className="text-brand-base-clara flex-shrink-0" size={20} />
                  <span className="font-sans text-brand-base-clara">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mensagem Central Section */}
      <section className="py-24 lg:py-32 bg-gradient-to-br from-brand-base-clara via-[#E2D6C0] to-brand-surface relative overflow-hidden" data-testid="sobre-mensagem">
        {/* Subtle texture */}
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: `repeating-linear-gradient(
            -45deg,
            transparent,
            transparent 80px,
            rgba(184, 142, 106, 0.05) 80px,
            rgba(184, 142, 106, 0.05) 160px
          )`
        }} />
        
        <div className="relative max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <div className="w-12 h-[2px] bg-brand-terracota mx-auto mb-8" />
          <blockquote className="font-serif text-2xl lg:text-3xl xl:text-4xl text-brand-neutro leading-relaxed mb-8">
            "Psicologia organizacional aplicada para alinhar{" "}
            <span className="italic text-brand-terracota">pessoas, cultura e resultados</span>
            {" "}com mais clareza, saúde e estratégia."
          </blockquote>
          <div className="w-12 h-[2px] bg-brand-terracota mx-auto" />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-28 bg-brand-surface" data-testid="sobre-cta">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl lg:text-4xl font-normal text-brand-neutro mb-6">
            Vamos conversar sobre sua empresa?
          </h2>
          <p className="font-sans text-brand-muted leading-relaxed mb-10 max-w-2xl mx-auto">
            Agende um diagnóstico estratégico e descubra como podemos ajudar sua organização a crescer de forma saudável e sustentável.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://wa.me/5586998325746?text=Olá! Gostaria de conhecer melhor a consultoria e agendar uma conversa."
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-sans font-medium"
              data-testid="sobre-cta-primary"
            >
              Agendar Conversa
              <ArrowRight size={18} />
            </a>
            <Link
              to="/servicos"
              className="btn-secondary inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-sans font-medium"
              data-testid="sobre-cta-secondary"
            >
              Conhecer Serviços
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SobrePage;
